import { signalingUserLeft, signalingWaitingUserJoined } from 'features/users/actions';
import { call, put, select } from 'redux-saga/effects';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { waitingEntryNotificationUpdateRequested } from 'features/notifications/actions';
import { selectMainRoomId } from 'features/breakout-rooms/breakoutRoomsSlice';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { selectLobbyJoinSoundEnabled } from 'features/room/roomSlice';
import { lobbyJoinSoundManager } from 'utils/audio/LobbyJoinSoundManager';
import { sendWaitingUsersJoinedSaga } from 'features/users/sagas/sendWaitingUsersJoinedSaga';
import { isParticipantsPanelAccessible } from 'features/side-panels/sagas/filterAccessiblePanels';
import { onUserLeftSaga } from 'features/users/sagas/onUserLeftSaga';
import { selectAvailableUserIdsWithoutLocal } from 'features/users/usersSlice';
import { UserId } from 'features/users/types';

export function* onWaitingUserJoinedSaga(action: ReturnType<typeof signalingWaitingUserJoined>) {
  try {
    const availableIds: UserId[] = yield select(selectAvailableUserIdsWithoutLocal);
    if (availableIds.includes(action.payload.id)) {
      // the server re-listed an active participant as waiting (reconnect into a
      // locked room); run the full departure path first — blocking call, so the
      // E2EE cleanup below cannot race the lobby key exchange
      yield call(onUserLeftSaga, signalingUserLeft({ id: action.payload.id }));
    }

    const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
    if (e2eeEnabled) {
      const mainRoomId: string = yield select(selectMainRoomId);
      yield call(
        E2EEManager.exchangeEncryptionKey,
        action.payload.id,
        mainRoomId,
        action.payload.e2eePublicKey!
      );
    }

    yield call(sendWaitingUsersJoinedSaga, [action.payload]);

    const canControlRoomEntry: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.controlRoomEntry
    );
    const canAccessParticipantsPanel: boolean = yield call(isParticipantsPanelAccessible);
    const isLobbyJoinSoundEnabled: boolean = yield select(selectLobbyJoinSoundEnabled);

    if (canControlRoomEntry && canAccessParticipantsPanel && isLobbyJoinSoundEnabled) {
      yield call(lobbyJoinSoundManager.triggerSound);
    }

    yield put(waitingEntryNotificationUpdateRequested());
  } catch (error) {
    Sentry.captureException(error);
    logger
      .remote({ tier: 1 })
      .error(`Cannot handle waiting entry user id=${action.payload.id}`, error);
  }
}
